import React, { useEffect, useState, useMemo } from 'react';
import { useAtom } from 'jotai';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, LayoutDashboard, Loader2, Trash2, Eye, Home as HomeDashboardIcon, Star as StarIcon } from 'lucide-react';
import { DashboardService } from '../services/dashboardService';
import { userService } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { currentOrganizationAtom } from '../store/organizationAtoms';
import {
  dashboardsAtom,
  dashboardsLoadingAtom,
  refreshDashboardsAtom,
  dashboardFormDialogAtom
} from '../store/dashboardAtoms';
import type { CreateDashboardData, DashboardLayoutConfiguration, DashboardChartItem } from '@shared/types/dashboard.types';
import type { User as UserProfileType } from '@shared/schemas/user';
import { Link } from 'react-router-dom';
import EditableChartTitle from '../components/charts/EditableChartTitle';
import EditableDescription from '../components/charts/EditableDescription';

const DashboardsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [currentOrganization] = useAtom(currentOrganizationAtom);
  const [dashboards, setDashboards] = useAtom(dashboardsAtom);
  const [isLoading, setIsLoading] = useAtom(dashboardsLoadingAtom);
  const [refreshTrigger, setRefreshTrigger] = useAtom(refreshDashboardsAtom);
  const [formDialogState, setFormDialogState] = useAtom(dashboardFormDialogAtom);

  const [newDashboardTitle, setNewDashboardTitle] = useState('');
  const [newDashboardDescription, setNewDashboardDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (currentUser && currentOrganization) {
        setIsLoading(true);
        try {
          const [fetchedDashboards, fetchedUserProfile] = await Promise.all([
            DashboardService.getUserDashboards(currentOrganization.id, currentUser.uid),
            userService.getById(currentUser.uid)
          ]);
          setDashboards(fetchedDashboards);
          setUserProfile(fetchedUserProfile);
        } catch (error) {
          console.error('Error fetching initial dashboards page data:', error);
          setDashboards([]);
          setUserProfile(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setDashboards([]);
        setUserProfile(null);
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [currentUser, currentOrganization, refreshTrigger, setDashboards, setIsLoading]);

  const openCreateDashboardDialog = () => {
    setNewDashboardTitle('');
    setNewDashboardDescription('');
    setFormDialogState({ isOpen: true, mode: 'create', dashboard: null });
  };

  const handleCreateDashboard = async () => {
    if (!currentUser || !currentOrganization || !newDashboardTitle.trim()) {
      console.error('User, organization, or title is missing');
      return;
    }
    setIsSubmitting(true);
    try {
      const initialLayout: DashboardLayoutConfiguration = { columns: 3 };
      const initialCharts: DashboardChartItem[] = [];
      
      const dashboardDataPayload: Partial<CreateDashboardData> = {
        organizationId: currentOrganization.id,
        userId: currentUser.uid,
        title: newDashboardTitle.trim(),
        layoutConfiguration: initialLayout,
        charts: initialCharts,
      };

      const trimmedDescription = newDashboardDescription.trim();
      if (trimmedDescription) {
        dashboardDataPayload.description = trimmedDescription;
      }
      // Fields like description are now conditionally added, so they won't be undefined if empty.

      await DashboardService.createDashboard(currentOrganization.id, currentUser.uid, dashboardDataPayload as CreateDashboardData);
      setRefreshTrigger(prev => prev + 1);
      setFormDialogState({ isOpen: false, mode: 'create' });
    } catch (error) {
      console.error('Error creating dashboard:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    if (!currentUser || !currentOrganization) return;
    console.log('Attempting to delete:', dashboardId);
    try {
        await DashboardService.deleteDashboard(currentOrganization.id, currentUser.uid, dashboardId);
        setRefreshTrigger(prev => prev + 1);
    } catch (error) {
        console.error("Error deleting dashboard", error);
    }
  };

  const handleSetDefaultDashboard = async (dashboardId: string | null) => {
    if (!currentUser) return;
    try {
      await userService.update(currentUser.uid, { defaultDashboardId: dashboardId });
      setUserProfile(prevProfile => prevProfile ? { ...prevProfile, defaultDashboardId: dashboardId } : null);
    } catch (error) {
      console.error('Error setting default dashboard:', error);
    }
  };

  const handleToggleFavoriteDashboard = async (dashboardId: string) => {
    if (!currentUser || !userProfile) return;
    try {
      await userService.toggleFavoriteDashboard(currentUser.uid, dashboardId);
      // Optimistically update local userProfile state
      const currentFavorites = userProfile.favoriteDashboardIds || [];
      let updatedFavorites: string[];
      if (currentFavorites.includes(dashboardId)) {
        updatedFavorites = currentFavorites.filter(id => id !== dashboardId);
      } else {
        updatedFavorites = [...currentFavorites, dashboardId];
      }
      setUserProfile(prev => prev ? ({ ...prev, favoriteDashboardIds: updatedFavorites }) : null);
    } catch (error) {
      console.error('Error toggling favorite dashboard:', error);
      // TODO: Add user feedback
    }
  };

  const handleUpdateDashboardTitle = async (dashboardId: string, newTitle: string) => {
    if (!currentUser || !currentOrganization) return;
    try {
      await DashboardService.updateDashboard(currentOrganization.id, currentUser.uid, dashboardId, { title: newTitle });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating dashboard title:', error);
      // Add user feedback
    }
  };

  const handleUpdateDashboardDescription = async (dashboardId: string, newDescription: string) => {
    if (!currentUser || !currentOrganization) return;
    try {
      await DashboardService.updateDashboard(currentOrganization.id, currentUser.uid, dashboardId, { description: newDescription });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating dashboard description:', error);
      // Add user feedback
    }
  };

  const favoritedDashboards = useMemo(() => {
    if (!userProfile || !dashboards.length) return [];

    const explicitFavoriteIds = userProfile.favoriteDashboardIds || [];
    const combinedFavoriteIds = new Set(explicitFavoriteIds);

    if (userProfile.defaultDashboardId) {
      combinedFavoriteIds.add(userProfile.defaultDashboardId);
    }
    // Need to ensure the order of favorites is somewhat preserved or follows a logic, e.g., default first, then by favorite date/order
    // For now, just filtering based on the combined set and then relying on the general sort for display.
    const favs = dashboards.filter(d => combinedFavoriteIds.has(d.id));
    
    // Optional: if you want the default dashboard to always appear first in the favorites list if present.
    if (userProfile.defaultDashboardId) {
        const defaultDash = favs.find(d => d.id === userProfile.defaultDashboardId);
        if (defaultDash) {
            return [defaultDash, ...favs.filter(d => d.id !== userProfile.defaultDashboardId)];
        }
    }
    return favs; // Fallback to whatever order filter produces if default isn't in favs (shouldn't happen with .add())

  }, [dashboards, userProfile]);

  return (
    <Sidebar>
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <LayoutDashboard className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold leading-tight text-foreground">Dashboards</h1>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground">
                Organize and visualize your charts by creating custom dashboards.
              </p>
            </div>
          </div>
          <Button onClick={openCreateDashboardDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create Dashboard
          </Button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading dashboards...</span>
          </div>
        )}

        {!isLoading && favoritedDashboards.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4 pb-2 border-b">Favorites</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favoritedDashboards.map((dashboard) => {
                const isDefault = userProfile?.defaultDashboardId === dashboard.id;
                const isExplicitFavorite = userProfile?.favoriteDashboardIds?.includes(dashboard.id) || false;
                return (
                  <Card key={`fav-${dashboard.id}`} className={`flex flex-col ${isDefault ? 'border-primary shadow-lg' : ''}`}>
                    <CardHeader className="flex-grow pb-2">
                      <EditableChartTitle
                        title={dashboard.title}
                        onSave={(newTitle) => handleUpdateDashboardTitle(dashboard.id, newTitle)}
                        textClassName="text-lg font-semibold text-foreground hover:text-primary transition-colors"
                        inputClassName="text-lg font-semibold"
                      />
                      <EditableDescription
                        description={dashboard.description}
                        onSave={(newDesc) => handleUpdateDashboardDescription(dashboard.id, newDesc)}
                        placeholder="Add a description..."
                        textClassName="line-clamp-2 mt-1 text-sm text-muted-foreground"
                        textareaClassName="text-sm"
                        rows={2}
                      />
                    </CardHeader>
                    <CardContent className="mt-auto pt-2 flex flex-col justify-end">
                      <div className="mb-3">
                        <p className="text-sm text-foreground font-medium">{dashboard.charts.length} chart{dashboard.charts.length === 1 ? '' : 's'}</p>
                        <p className="text-xs text-muted-foreground">Last updated: {new Date(dashboard.updatedAt.toDate()).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/organization/${currentOrganization?.id}/dashboards/${dashboard.id}`}><Eye className="h-4 w-4 mr-1.5"/>View</Link>
                        </Button>
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${isExplicitFavorite ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-400'}`} onClick={() => handleToggleFavoriteDashboard(dashboard.id)} title={isExplicitFavorite ? 'Remove from Favorites' : 'Add to Favorites'}>
                            <StarIcon className={`h-4 w-4 ${isExplicitFavorite ? 'fill-amber-400' : ''}`} />
                          </Button>
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${isDefault ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`} onClick={() => handleSetDefaultDashboard(isDefault ? null : dashboard.id)} title={isDefault ? 'Default Dashboard (Click to clear)' : 'Set as Default'}><HomeDashboardIcon className={`h-4 w-4 ${isDefault ? 'fill-current' : ''}`} /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive-hover h-8 w-8" onClick={() => handleDeleteDashboard(dashboard.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {!isLoading && dashboards.length === 0 && (
          <Card className="text-center py-12 border-2 border-dashed border-primary/20 bg-gradient-to-br from-primary/5 to-orange/5">
            <CardContent>
              <LayoutDashboard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Dashboards Yet</h3>
              <p className="text-muted-foreground mb-6">
                Get started by creating your first dashboard to arrange your charts.
              </p>
              <Button onClick={openCreateDashboardDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && dashboards.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {favoritedDashboards.length > 0 ? 'All Dashboards' : 'Dashboards'} ({dashboards.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboards.map((dashboard) => {
                const isDefault = userProfile?.defaultDashboardId === dashboard.id;
                const isExplicitFavorite = userProfile?.favoriteDashboardIds?.includes(dashboard.id) || false;
                return (
                  <Card key={dashboard.id} className={`flex flex-col ${isDefault ? 'border-primary shadow-lg' : ''}`}>
                    <CardHeader className="flex-grow pb-2">
                      <EditableChartTitle
                        title={dashboard.title}
                        onSave={(newTitle) => handleUpdateDashboardTitle(dashboard.id, newTitle)}
                        textClassName="text-lg font-semibold text-foreground hover:text-primary transition-colors"
                        inputClassName="text-lg font-semibold"
                      />
                      <EditableDescription
                        description={dashboard.description}
                        onSave={(newDesc) => handleUpdateDashboardDescription(dashboard.id, newDesc)}
                        placeholder="Add a description..."
                        textClassName="line-clamp-2 mt-1 text-sm text-muted-foreground"
                        textareaClassName="text-sm"
                        rows={2}
                      />
                    </CardHeader>
                    <CardContent className="mt-auto pt-2 flex flex-col justify-end">
                      <div className="mb-3">
                        <p className="text-sm text-foreground font-medium">{dashboard.charts.length} chart{dashboard.charts.length === 1 ? '' : 's'}</p>
                        <p className="text-xs text-muted-foreground">Last updated: {new Date(dashboard.updatedAt.toDate()).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/organization/${currentOrganization?.id}/dashboards/${dashboard.id}`}><Eye className="h-4 w-4 mr-1.5"/>View</Link>
                        </Button>
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${isExplicitFavorite ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-400'}`} onClick={() => handleToggleFavoriteDashboard(dashboard.id)} title={isExplicitFavorite ? 'Remove from Favorites' : 'Add to Favorites'}>
                            <StarIcon className={`h-4 w-4 ${isExplicitFavorite ? 'fill-amber-400' : ''}`} />
                          </Button>
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${isDefault ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`} onClick={() => handleSetDefaultDashboard(isDefault ? null : dashboard.id)} title={isDefault ? 'Default Dashboard (Click to clear)' : 'Set as Default'}><HomeDashboardIcon className={`h-4 w-4 ${isDefault ? 'fill-current' : ''}`} /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive-hover h-8 w-8" onClick={() => handleDeleteDashboard(dashboard.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <Dialog open={formDialogState.isOpen} onOpenChange={(isOpen) => setFormDialogState(prev => ({ ...prev, isOpen }))}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{formDialogState.mode === 'create' ? 'Create New Dashboard' : 'Edit Dashboard'}</DialogTitle>
            <DialogDescription>
              {formDialogState.mode === 'create' 
                ? 'Give your new dashboard a title and an optional description.'
                : 'Update the details for your dashboard.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dashboard-title" className="text-right">
                Title
              </Label>
              <Input
                id="dashboard-title"
                value={newDashboardTitle}
                onChange={(e) => setNewDashboardTitle(e.target.value)}
                className="col-span-3"
                placeholder="e.g., Monthly Sales Performance"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dashboard-description" className="text-right">
                Description
              </Label>
              <Textarea
                id="dashboard-description"
                value={newDashboardDescription}
                onChange={(e) => setNewDashboardDescription(e.target.value)}
                className="col-span-3"
                placeholder="A brief description of what this dashboard shows."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
                <Button type="button" variant="outline" onClick={() => setFormDialogState({isOpen: false, mode: 'create'})}>Cancel</Button>
            </DialogClose>
            <Button type="submit" onClick={handleCreateDashboard} disabled={isSubmitting || !newDashboardTitle.trim()}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {formDialogState.mode === 'create' ? 'Create Dashboard' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
};

export default DashboardsPage; 